/**
 * The shared wizard step bar (BL-1801, ratifying a new `StepBar` row of the
 * ADR 030 Canon — Design-approval granted in session by Dan Saper, 2026-09-18).
 *
 * Collapses bidder-web's two hand-rolled progress indicators — the
 * Register-to-bid modal's labeled strip (`registration/StepBar.tsx`, which
 * overflowed the modal at every width and scrolled sideways) and the
 * onboarding page's inline circles-with-titles block — onto one structure:
 * a row of small circles joined by short lines, plus ONE label naming the
 * current step ("Card · step 5 of 6"). The bar lists only the steps the
 * bidder FILLS IN — a result screen ("You're registered") is not a step and
 * gets no circle (BUG-1665: the seventh circle BL-1801 gave it did not fit
 * the modal at 375px). Six circles are ~265px wide, so the row fits a phone;
 * the label sits beside the row where there is room and wraps beneath it
 * where there is not. One design at every width (Michael Lawrie's ruling on
 * the BL-1801 prototype) — there is no mobile variant.
 *
 * WHAT the core decides, and the skins may not:
 *   - the state rule: a step is DONE when it sits before the current index —
 *     or, once the caller marks the wizard COMPLETE (its result screen is
 *     showing), every step is done and none is current, so the bar reads as a
 *     row of checks under a completion label ("Registered · 6 of 6 complete");
 *   - the glyph rule: done → check, otherwise → its 1-based number. Done and
 *     pending therefore differ by shape, never by colour alone (ADR 051);
 *   - navigation: only DONE circles are clickable, and only when the caller
 *     hands over `onStepClick`. Pending circles and the current one are
 *     disabled buttons, so a tap on the future is inert by construction;
 *   - a11y: the bar is a `<nav aria-label="Progress">`, every circle carries
 *     its step label as its accessible name (the label text is rendered for
 *     the current step only), the current circle is `aria-current="step"`,
 *     and the label region is polite-live so a screen reader hears the step
 *     change.
 *
 * Appearance is a `dialect` object of class names per ADR 030 Decision 2 —
 * the skin binds its app's sizes, colours and type; the core never names a
 * colour. The tenant brand is only known at runtime, so it rides
 * `activeStyle` (the sanctioned dynamic-value inline-style carve-out, same
 * as `Avatar` / `RadioButton`), applied to the current circle only.
 */
'use client';

import clsx from 'clsx';
import { Check } from 'lucide-react';
import { Fragment, type CSSProperties } from 'react';

/** One step as the bar sees it — an id for keys plus the human label. */
export interface StepBarStep {
    /** Stable key; also emitted as `data-step`. */
    id: string;
    /** The step's name — the current step's visible label, every circle's accessible name. */
    label: string;
}

/**
 * Per-app appearance binding — class names only, no structure.
 *
 * Every slot is required: a bar with no circle size or state colours has
 * nothing to draw, so there is no meaningful bare default.
 */
export interface StepBarDialect {
    /** The `<nav>` — layout of row + label, wrap behaviour, gaps. */
    root: string;
    /** The circles row — flex, alignment, the gap between circle and line. */
    row: string;
    /** A connector between two circles. */
    line: string;
    /** Added to a connector that follows a done circle. */
    lineDone: string;
    /** Every circle — box, centring, type, radius, reset. */
    circle: string;
    /** Added to a done circle (fill + check colour). */
    circleDone: string;
    /** Added to the current circle (the brand fill lives in `activeStyle`). */
    circleActive: string;
    /** Added to a pending circle. */
    circlePending: string;
    /** The current step's name. */
    label: string;
    /** The " · step n of N" suffix. */
    labelMuted: string;
}

/** Props for the shared StepBar core. Skins re-export their own app-facing shape. */
export interface StepBarProps {
    /** Every step the bidder fills in, in order — a result screen is not a step and is not listed. */
    steps: readonly StepBarStep[];
    /** Index into `steps` of the step being shown. Ignored while `complete`. */
    currentIndex: number;
    /**
     * The wizard has finished and its result screen is showing: every circle
     * renders as a check, none is current or clickable, and the label becomes
     * `{completeLabel} · N of N complete`.
     */
    complete?: boolean;
    /** The completion label's name; defaults to "Complete". Read only while `complete`. */
    completeLabel?: string;
    /**
     * Called with a DONE step's index when its circle is tapped. Omitting it
     * makes the bar display-only (every circle disabled).
     */
    onStepClick?: (index: number) => void;
    /** Inline style for the current circle — the runtime tenant brand fill + contrast text. */
    activeStyle?: CSSProperties;
    /** Extra classes on the `<nav>` — the caller's spacing lives here. */
    className?: string;
    /** Inline style on the `<nav>` — the sanctioned slot for a runtime CSS variable (e.g. a tenant brand the dialect's classes read). */
    style?: CSSProperties;
    /** Emitted as `data-testid` on the `<nav>`; defaults to `step-bar` (one bar per surface). */
    testId?: string;
    /**
     * BL-1910 — where step names show. `'current'` (default): one label naming
     * the current step ("Card · step 5 of 6"). `'inline'`: every step's name
     * beside its circle ("1 Choose changes — 2 Review"), for a short wizard
     * where the whole path fits — the current name in `label`, the rest in
     * `labelMuted`. The live region still announces the current step.
     */
    labels?: 'current' | 'inline';
    /** The app's appearance binding. */
    dialect: StepBarDialect;
}

/**
 * A row of step circles plus one current-step label, appearance bound by the
 * skin's dialect. Never scrolls horizontally: the label wraps under the row
 * when the container is too narrow for both.
 *
 * @param props - {@link StepBarProps}
 * @returns One `<nav aria-label="Progress">`.
 */
export function StepBar({
    steps, currentIndex, complete = false, completeLabel = 'Complete', onStepClick, activeStyle, className, style, testId = 'step-bar', dialect,
    labels = 'current',
}: StepBarProps) {
    const inline = labels === 'inline';
    const total = steps.length;
    const lastIndex = total - 1;
    const current = complete ? undefined : steps[currentIndex];
    const isDone = (i: number) => complete || i < currentIndex;

    return (
        <nav aria-label="Progress" data-testid={testId} style={style} className={clsx(dialect.root, className)}>
            <div className={dialect.row}>
                {steps.map((step, i) => {
                    const done = isDone(i);
                    const active = !complete && i === currentIndex;
                    const clickable = !complete && done && onStepClick != null;
                    return (
                        <Fragment key={step.id}>
                            <button
                                type="button"
                                data-step={step.id}
                                aria-label={step.label}
                                aria-current={active ? 'step' : undefined}
                                disabled={!clickable}
                                onClick={clickable ? () => onStepClick(i) : undefined}
                                style={active ? activeStyle : undefined}
                                className={clsx(
                                    dialect.circle,
                                    done && dialect.circleDone,
                                    active && dialect.circleActive,
                                    !done && !active && dialect.circlePending,
                                    clickable && 'cursor-pointer',
                                )}
                            >
                                {done ? <Check aria-hidden="true" className="h-3 w-3" /> : i + 1}
                            </button>
                            {inline && (
                                <span aria-hidden="true" data-step-label={step.id} className={active ? dialect.label : dialect.labelMuted}>
                                    {step.label}
                                </span>
                            )}
                            {i < lastIndex && (
                                <span aria-hidden="true" className={clsx(dialect.line, done && dialect.lineDone)} />
                            )}
                        </Fragment>
                    );
                })}
            </div>
            {complete ? (
                <div aria-live="polite">
                    <span className={dialect.label}>{completeLabel}</span>
                    <span className={dialect.labelMuted}>{` · ${total} of ${total} complete`}</span>
                </div>
            ) : current != null && (
                <div aria-live="polite" className={inline ? 'sr-only' : undefined}>
                    <span className={dialect.label}>{current.label}</span>
                    <span className={dialect.labelMuted}>{` · step ${currentIndex + 1} of ${total}`}</span>
                </div>
            )}
        </nav>
    );
}
